import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { extractErrorMessage } from '@/lib/api-client';
import {
  SUPPLIER_TYPE_LABELS,
  useCreateSupplier,
  useUpdateSupplier,
  type Supplier,
  type SupplierType,
} from './api';

interface SupplierFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  supplier?: Supplier | null;
}

export function SupplierFormDialog({
  open,
  onOpenChange,
  supplier,
}: SupplierFormDialogProps) {
  const isEdit = !!supplier;

  const [name, setName] = useState('');
  const [type, setType] = useState<SupplierType>('workshop');
  const [cnpj, setCnpj] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [notes, setNotes] = useState('');
  const [active, setActive] = useState(true);

  const create = useCreateSupplier();
  const update = useUpdateSupplier(supplier?.id ?? '');
  const pending = create.isPending || update.isPending;

  useEffect(() => {
    if (!open) return;
    if (supplier) {
      setName(supplier.name);
      setType(supplier.type);
      setCnpj(supplier.cnpj ?? '');
      setPhone(supplier.phone ?? '');
      setEmail(supplier.email ?? '');
      setAddress(supplier.address ?? '');
      setCity(supplier.city ?? '');
      setState(supplier.state ?? '');
      setNotes(supplier.notes ?? '');
      setActive(supplier.active);
    } else {
      setName('');
      setType('workshop');
      setCnpj('');
      setPhone('');
      setEmail('');
      setAddress('');
      setCity('');
      setState('');
      setNotes('');
      setActive(true);
    }
  }, [open, supplier]);

  const canSubmit = name.trim().length >= 2 && !pending;

  const handleSubmit = async () => {
    const payload = {
      name: name.trim(),
      type,
      cnpj: cnpj.trim() || undefined,
      phone: phone.trim() || undefined,
      email: email.trim() || undefined,
      address: address.trim() || undefined,
      city: city.trim() || undefined,
      state: state.trim().toUpperCase() || undefined,
      notes: notes.trim() || undefined,
      active,
    };
    try {
      if (isEdit && supplier) {
        await update.mutateAsync(payload);
        toast.success(`Fornecedor "${name.trim()}" atualizado`);
      } else {
        await create.mutateAsync(payload);
        toast.success(`Fornecedor "${name.trim()}" cadastrado`);
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Editar fornecedor' : 'Novo fornecedor'}
          </DialogTitle>
          <DialogDescription>
            Oficinas, lojas de peças, borracharias e outros prestadores.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="name">Nome *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex.: Auto Mecânica Silva"
                maxLength={120}
                autoFocus
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="type">Tipo *</Label>
              <Select
                value={type}
                onValueChange={(v) => setType(v as SupplierType)}
              >
                <SelectTrigger id="type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(SUPPLIER_TYPE_LABELS) as SupplierType[]).map(
                    (t) => (
                      <SelectItem key={t} value={t}>
                        {SUPPLIER_TYPE_LABELS[t]}
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cnpj">CNPJ</Label>
              <Input
                id="cnpj"
                value={cnpj}
                onChange={(e) => setCnpj(e.target.value)}
                placeholder="00.000.000/0000-00"
                maxLength={18}
                className="font-mono"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="phone">Telefone</Label>
              <Input
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+5511988887777"
                maxLength={20}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="contato@fornecedor.com.br"
                maxLength={255}
              />
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="address">Endereço</Label>
              <Input
                id="address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Rua, número, bairro"
                maxLength={255}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="city">Cidade</Label>
              <Input
                id="city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="São Paulo"
                maxLength={120}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="state">UF</Label>
              <Input
                id="state"
                value={state}
                onChange={(e) =>
                  setState(e.target.value.toUpperCase().slice(0, 2))
                }
                placeholder="SP"
                maxLength={2}
                className="font-mono"
              />
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="notes">Observações</Label>
              <Input
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Opcional: horário, especialidade, contato preferido..."
              />
            </div>
          </div>

          <label className="flex cursor-pointer items-center gap-2 pt-2 text-sm">
            <input
              type="checkbox"
              className="size-4 rounded border-border"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
            />
            Fornecedor ativo (disponível para uso em novas OS)
          </label>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {pending
              ? isEdit
                ? 'Salvando…'
                : 'Cadastrando…'
              : isEdit
                ? 'Salvar alterações'
                : 'Cadastrar fornecedor'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
